// Shared role-set constants for the AI Prompting panel. Both the client
// (panel gating, "Suggest" vs "Direct edit" affordances) and the server
// (override + suggestion-decide endpoints) read from here so the two sides
// can't drift.
//
// See docs/planning/ai_prompting_suggest_edit.md for the locked answers.

/**
 * Any user with one of these roles can SEE the AI prompting panel and
 * POST a suggestion. The decision to apply (publish an override or
 * accept/merge a suggestion) is a stricter set — `AI_PROMPTING_APPLY_ROLES`.
 *
 * Kept in sync with `TAB_ACCESS.aiPrompting` in `LeadWorkspaceHeaderAccess.ts`.
 */
export const AI_PROMPTING_VIEW_ROLES = [
  "trainer",
  "admin",
  "owner",
  "dev",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

/**
 * Roles allowed to publish overrides AND to accept / reject / merge
 * suggestions. Per the locked plan: reuse the existing `trainer` role
 * because prompts are org-isolated. Platform roles are always permitted.
 */
export const AI_PROMPTING_APPLY_ROLES = [
  "trainer",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

export type AiPromptingViewRole = (typeof AI_PROMPTING_VIEW_ROLES)[number];
export type AiPromptingApplyRole = (typeof AI_PROMPTING_APPLY_ROLES)[number];

const VIEW_SET = new Set<string>(AI_PROMPTING_VIEW_ROLES);
const APPLY_SET = new Set<string>(AI_PROMPTING_APPLY_ROLES);

export function isAiPromptingViewRole(role: unknown): role is AiPromptingViewRole {
  return typeof role === "string" && VIEW_SET.has(role);
}

export function isAiPromptingApplyRole(role: unknown): role is AiPromptingApplyRole {
  return typeof role === "string" && APPLY_SET.has(role);
}

/** Convenience: does `roles[]` (e.g. from `user_roles`) include ANY apply role? */
export function rolesIncludeAiPromptingApply(roles: readonly string[] | null | undefined): boolean {
  if (!roles?.length) return false;
  for (const r of roles) {
    if (APPLY_SET.has(r)) return true;
  }
  return false;
}
